
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Receipt, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import UniversalStatusBadge from '@/components/shared/UniversalStatusBadge';
import { computeAdjustedEstimateTotalAsync } from '@/utils/estimateTotalCalculation';

interface ProjectInvoicesProps {
  projectId: string;
  estimateId?: string;
}

const ProjectInvoices: React.FC<ProjectInvoicesProps> = ({ projectId, estimateId }) => {
  const navigate = useNavigate();

  const { data: invoices, isLoading, refetch } = useQuery({
    queryKey: ['projectInvoices', estimateId],
    queryFn: async () => {
      if (!estimateId) return [];
      
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          customers(name, last_name)
        `)
        .eq('estimate_id', estimateId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!estimateId,
    refetchInterval: 30000, // Refetch every 30 seconds to catch payment updates
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Fetch estimate status for conditional rendering
  const { data: estimateStatus } = useQuery({
    queryKey: ['estimateStatus', estimateId],
    queryFn: async () => {
      if (!estimateId) return null;
      const { data } = await supabase
        .from('estimates')
        .select('status')
        .eq('id', estimateId)
        .single();
      return data?.status || null;
    },
    enabled: !!estimateId,
  });

  const { data: adjustedEstimateTotal } = useQuery({
    queryKey: ['adjustedEstimateTotal', estimateId],
    queryFn: async () => {
      if (!estimateId) return 0;
      
      // Fetch estimate with adjustment_metadata
      const { data: estimateData, error: estimateError } = await supabase
        .from('estimates')
        .select('*, main_version_id, adjustment_metadata')
        .eq('id', estimateId)
        .single();

      if (estimateError) throw estimateError;

      const mainVersionId = estimateData?.main_version_id;

      // Get line items from main version snapshot or current
      let lineItemsForCalc: any[] = [];

      if (mainVersionId) {
        const { data: mainVersion } = await supabase
          .from('estimate_versions')
          .select('line_items_snapshot')
          .eq('id', mainVersionId)
          .maybeSingle();

        if (mainVersion?.line_items_snapshot && Array.isArray(mainVersion.line_items_snapshot) && mainVersion.line_items_snapshot.length > 0) {
          lineItemsForCalc = (mainVersion.line_items_snapshot as any[]).map((item: any) => ({
            ...item,
            total_cost_snapshot: item.total_cost_snapshot ?? item.total_cost ?? 0,
            quantity_in_base_units: item.quantity_in_base_units ?? item.quantity ?? 0,
          }));
        }
      }

      if (lineItemsForCalc.length === 0) {
        const { data: currentLineItems } = await supabase
          .from('estimate_line_items')
          .select('*')
          .eq('estimate_id', estimateId);
        lineItemsForCalc = currentLineItems || [];
      }

      const result = await computeAdjustedEstimateTotalAsync(
        lineItemsForCalc,
        estimateData?.adjustment_metadata as any
      );
      return result.adjustedSubtotal;
    },
    enabled: !!estimateId,
  });

  const handleInvoiceClick = (invoiceId: string) => {
    navigate(`/invoices/${invoiceId}`);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Project Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>Loading invoices...</p>
        </CardContent>
      </Card>
    );
  }

  // Calculate financial summary
  const totalInvoiced = invoices?.reduce((sum, invoice) => sum + (invoice.total_amount || 0), 0) || 0;
  const totalPaid = invoices?.reduce((sum, invoice) => sum + (invoice.paid_amount || 0), 0) || 0;
  const totalOutstanding = totalInvoiced - totalPaid;
  
  // Use centralized adjusted estimate total
  const estimateTotal = adjustedEstimateTotal || 0;
  const remainingToInvoice = Math.max(0, estimateTotal - totalInvoiced);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          Project Invoices
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Financial Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
          <div className="text-center">
            <div className="text-sm text-muted-foreground">Total Invoiced</div>
            <div className="text-lg font-semibold">R {totalInvoiced.toLocaleString()}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-muted-foreground">Paid</div>
            <div className="text-lg font-semibold text-green-600">R {totalPaid.toLocaleString()}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-muted-foreground">Outstanding</div>
            <div className="text-lg font-semibold text-orange-600">R {totalOutstanding.toLocaleString()}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-muted-foreground">Remaining to Invoice</div>
            <div className="text-lg font-semibold text-blue-600">R {remainingToInvoice.toLocaleString()}</div>
          </div>
        </div>

        {/* Invoices List */}
        {!invoices || invoices.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No invoices have been created for this project yet.</p>
            {estimateStatus === 'approved' && (
              <Button 
                className="mt-4" 
                onClick={() => navigate(`/invoices/create?estimate_id=${estimateId}`)}
              >
                Create First Invoice
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <h4 className="font-medium">Invoices ({invoices.length})</h4>
            {invoices.map((invoice) => (
              <div
                key={invoice.id}
                className="flex flex-col p-4 border rounded-lg hover:bg-muted/50 cursor-pointer gap-2"
                onClick={() => handleInvoiceClick(invoice.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <div className="font-medium">
                        {invoice.invoice_number || 'INV-PENDING'}
                      </div>
                      <UniversalStatusBadge 
                        entityType="invoice" 
                        status={invoice.status} 
                        dateContext={invoice.due_date}
                        size="sm"
                      />
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 truncate">
                      {(invoice.title?.replace(/^Invoice for\s*/i, '') || 'Untitled') + ' INV'}
                    </div>
                    <div className="text-sm text-muted-foreground flex flex-col sm:flex-row">
                      <span><span>Created: {format(new Date(invoice.created_at), 'dd MMMM yyyy')}</span></span>
                      {invoice.due_date && (
                        <span className="sm:ml-4">
                          Due: {format(new Date(invoice.due_date), 'dd MMMM yyyy')}
                        </span>
                      )}
                    </div>
                  </div>
                  <ExternalLink className="h-4 w-4 ml-4 text-muted-foreground shrink-0" />
                </div>
                <div className="flex items-center gap-3 pt-1 border-t">
                  <div className="font-semibold whitespace-nowrap">
                    R {(invoice.total_amount || 0).toLocaleString()}
                  </div>
                  {invoice.paid_amount > 0 && (
                    <div className="text-sm text-green-600 whitespace-nowrap">
                      Paid: R {invoice.paid_amount.toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {estimateStatus === 'approved' && remainingToInvoice > 0 && (
              <Button 
                className="w-full mt-4" 
                onClick={() => navigate(`/invoices/create?estimate_id=${estimateId}`)}
              >
                Create New Invoice
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectInvoices;
